/**
 * Leitura da DPS (Declaração de Prestação de Serviço) do padrão nacional da NFS-e, no XML
 * que GET /user/invoices/{id}/nf-xml devolve. Os campos ficam como no XML (códigos e strings
 * crus); a tradução dos códigos e a formatação são responsabilidade de quem exibe.
 */

export interface DpsEndereco {
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  codigoMunicipio: string | null;
  cep: string | null;
}

export interface DpsParte {
  documento: string | null;
  tipoDocumento: 'CNPJ' | 'CPF' | null;
  nome: string | null;
  fone: string | null;
  email: string | null;
  endereco: DpsEndereco | null;
}

export interface Dps {
  id: string | null;
  homologacao: boolean;
  emissao: string | null;
  competencia: string | null;
  serie: string | null;
  numero: string | null;
  codigoMunicipioEmissao: string | null;
  prestador: DpsParte | null;
  regimeTributario: { simplesNacional: string | null; regimeEspecial: string | null };
  tomador: DpsParte | null;
  servico: {
    codigoMunicipioPrestacao: string | null;
    codigoTributacaoNacional: string | null;
    descricao: string | null;
    codigoNbs: string | null;
  };
  valores: {
    valorServico: number | null;
    issqn: { tributacao: string | null; retencao: string | null };
    pisCofins: {
      cst: string | null;
      baseCalculo: number | null;
      aliquotaPis: number | null;
      aliquotaCofins: number | null;
      valorPis: number | null;
      valorCofins: number | null;
      retencao: string | null;
    } | null;
    totalTributos: { federal: number | null; estadual: number | null; municipal: number | null } | null;
  };
  ibsCbs: { cst: string | null; classificacao: string | null } | null;
}

/**
 * Lê a DPS do XML. Procura o infDPS em qualquer nível, então aceita tanto a DPS solta quanto
 * a NFS-e autorizada (que embrulha a DPS). Lança erro se o XML for inválido ou não tiver DPS.
 */
export function parseDps(xml: string): Dps {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new Error('XML inválido');
  }
  const inf = doc.getElementsByTagNameNS('*', 'infDPS')[0];
  if (!inf) {
    throw new Error('DPS não encontrada no XML');
  }

  const valores = find(inf, 'valores');
  const pisCofins = find(valores, 'trib/tribFed/piscofins');
  const totTrib = find(valores, 'trib/totTrib/vTotTrib');
  const ibsCbs = find(inf, 'IBSCBS/valores/trib/gIBSCBS');

  return {
    id: inf.getAttribute('Id'),
    homologacao: text(inf, 'tpAmb') === '2',
    emissao: text(inf, 'dhEmi'),
    competencia: text(inf, 'dCompet'),
    serie: text(inf, 'serie'),
    numero: text(inf, 'nDPS'),
    codigoMunicipioEmissao: text(inf, 'cLocEmi'),
    prestador: parte(find(inf, 'prest')),
    regimeTributario: {
      simplesNacional: text(inf, 'prest/regTrib/opSimpNac'),
      regimeEspecial: text(inf, 'prest/regTrib/regEspTrib'),
    },
    tomador: parte(find(inf, 'toma')),
    servico: {
      codigoMunicipioPrestacao: text(inf, 'serv/locPrest/cLocPrestacao'),
      codigoTributacaoNacional: text(inf, 'serv/cServ/cTribNac'),
      descricao: text(inf, 'serv/cServ/xDescServ'),
      codigoNbs: text(inf, 'serv/cServ/cNBS'),
    },
    valores: {
      valorServico: num(valores, 'vServPrest/vServ'),
      issqn: {
        tributacao: text(valores, 'trib/tribMun/tribISSQN'),
        retencao: text(valores, 'trib/tribMun/tpRetISSQN'),
      },
      pisCofins: pisCofins && {
        cst: text(pisCofins, 'CST'),
        baseCalculo: num(pisCofins, 'vBCPisCofins'),
        aliquotaPis: num(pisCofins, 'pAliqPis'),
        aliquotaCofins: num(pisCofins, 'pAliqCofins'),
        valorPis: num(pisCofins, 'vPis'),
        valorCofins: num(pisCofins, 'vCofins'),
        retencao: text(pisCofins, 'tpRetPisCofins'),
      },
      totalTributos: totTrib && {
        federal: num(totTrib, 'vTotTribFed'),
        estadual: num(totTrib, 'vTotTribEst'),
        municipal: num(totTrib, 'vTotTribMun'),
      },
    },
    ibsCbs: ibsCbs && {
      cst: text(ibsCbs, 'CST'),
      classificacao: text(ibsCbs, 'cClassTrib'),
    },
  };
}

function parte(el: Element | null): DpsParte | null {
  if (!el) return null;
  const cnpj = text(el, 'CNPJ');
  const cpf = text(el, 'CPF');
  const end = find(el, 'end');
  return {
    documento: cnpj ?? cpf,
    tipoDocumento: cnpj ? 'CNPJ' : cpf ? 'CPF' : null,
    nome: text(el, 'xNome'),
    fone: text(el, 'fone'),
    email: text(el, 'email'),
    endereco: end && {
      logradouro: text(end, 'xLgr'),
      numero: text(end, 'nro'),
      complemento: text(end, 'xCpl'),
      bairro: text(end, 'xBairro'),
      codigoMunicipio: text(end, 'endNac/cMun'),
      cep: text(end, 'endNac/CEP'),
    },
  };
}

/** Desce por filhos diretos (`a/b/c`), ignorando o namespace: CST e valores se repetem em outros grupos. */
function find(parent: Element | null, path: string): Element | null {
  return path.split('/').reduce<Element | null>(
    (el, tag) => (el ? Array.from(el.children).find((c) => c.localName === tag) ?? null : null),
    parent,
  );
}

function text(parent: Element | null, path: string): string | null {
  return find(parent, path)?.textContent?.trim() || null;
}

function num(parent: Element | null, path: string): number | null {
  const value = text(parent, path);
  return value === null ? null : Number(value);
}
